import { DataType } from '../../../../models/queries/queries.models';
import { ArrayUtils } from '../../../../utilities/arrays';
import { PropertyMetaModel, AllEntityTypeConfigurationConstants } from '../../../../models/basics/basics.models';
import { Reflection } from '../../../../utilities/reflection';
import * as DOMPurify from "dompurify";
import { Guid } from '../../../../utilities/guid';
import { ConfigService } from '@rightship-tech/common-ui/services/basic/config.service';

export class TransformHtmlHelper {

    public static transformHtml(html: string,
        readonly: boolean,
        model: any,
        properties: PropertyMetaModel[],
        callback: (element: HTMLElement, property: string, dataType: DataType) => void = null,
        forceValidations: boolean = false,        
        toolTip: string = null): HTMLElement {
        if (!html || !model || !properties || properties.length == 0) {
            return null;
        }
        const props: { [prop: string]: PropertyMetaModel; } = {};
        properties.forEach(p => {
            props[p.camelCasePropertyName] = p;
        });
        const div = document.createElement('div');
        div.innerHTML = DOMPurify.sanitize(html);

        //Replace quill's default parent P tag with a div
        if (div && div.childNodes[0] && (<HTMLElement>div.childNodes[0]).tagName.toLowerCase() == 'p') {
            const parentPTag = div.getElementsByTagName("p")[0];

            if (parentPTag) {
                const answerDiv = document.createElement('div');
                answerDiv.className = 'inner';
                answerDiv.innerHTML = parentPTag.innerHTML;
                div.appendChild(answerDiv);
                div.removeChild(parentPTag);
            }
        }

        this.wrapSpan(div, 0, toolTip);
        const variables = $(div).find('.variable-placeholder').toArray().map(el => <HTMLElement>el).filter(x => x.innerText && x.innerText.trim().length > 0);
        const length = variables.length;
        model.remove = (arr, value) => {
            const index = ArrayUtils.findIndex(arr, x => value == x);
            if (index > -1) {
                arr.splice(index, 1);
            }
        }
        model.add = (prop: string) => {
            model[prop].push(null);
        }

        model.onBlur = (prop, value) => { 
            model[prop] = value;
        };

        model.onMultiLookupChanged = (prop: string) => {
            const keys = Object.keys(model[prop + "_obj"]);
            model[prop] = [];
            keys.forEach(key => {
                if (model[prop + "_obj"][key]) {
                    if (model[prop].indexOf(key) == -1) {
                        model[prop].push(key);
                    }
                }
            });
        }

        model.passChanged = ($event: CustomEvent) => {
            if ($event.detail.oldValue && $event.detail.oldValue != $event.detail.newValue) {
                model['isPasswordHashed'] = false;
            }
        }

        for (let index = length - 1; index >= 0; index--) {
            const element = variables[index];
            const propName = element.getAttribute('data-property-name');
            const className = Reflection.kebabCase(propName);
            if (props[propName]) {
                const parameter = props[propName].parameter;
                const secondParameter = props[propName].secondParameter;
                const isEditable = props[propName].isEditableByUser.toString() == "true";
                const isCollection = props[propName].isCollection;
                const togglePasswordEnabled = element.getAttribute('data-toggle-password');
                const startLabel = element.getAttribute('data-start-label');
                const endLabel = element.getAttribute('data-end-label');
                const min = element.getAttribute('data-min');
                const max = element.getAttribute('data-max');
                const step = element.getAttribute('data-step');
                let format = element.getAttribute('data-format');
                const placeholder = element.getAttribute('data-placeholder');
                const useNumberGrouping = element.getAttribute('data-use-number-grouping') != "false";

                let defaultValue: any = element.getAttribute('data-default-value');
                const lookupParameters = element.getAttribute('data-lookup-parameters') || secondParameter;
                const desktopSize = element.getAttribute('data-desktop-size');
                const tabletSize = element.getAttribute('data-tablet-size');
                const mobileSize = element.getAttribute('data-mobile-size');
                const lookupStyle = element.getAttribute('data-lookup-style');
                const hideEmptyOption = element.getAttribute('data-hide-empty-option');
                const multiLookupStyle = element.getAttribute('data-multi-lookup-style');
                const exclusiveAnswers = element.getAttribute('data-exclusive-answers');
                const textboxStyle = element.getAttribute('data-textbox-style');
                const checkboxStyle = element.getAttribute('data-checkbox-style');
                const naOption = element.getAttribute('data-na-option');
                const multiline = element.getAttribute('data-multiline') == "true";
                const entityFinderParameters = element.getAttribute('data-entity-finder-parameters') || secondParameter;

                const type: DataType = props[propName].type;
                let elementTransformed: HTMLElement = null;
                const value = "model." + propName;
                if (!isCollection) {
                    switch (type) {
                        case DataType.Boolean: {
                            if (!checkboxStyle || checkboxStyle == "check-box") {
                                elementTransformed = document.createElement('check-box');
                                elementTransformed.setAttribute('checked.two-way', !forceValidations && (readonly || !isEditable) ? value : `${value} & validate`);
                                elementTransformed.setAttribute('readonly.bind', (readonly || !isEditable).toString());
                            } else {
                                elementTransformed = document.createElement('yes-no-control');
                                elementTransformed.setAttribute('checked.two-way', !forceValidations && (readonly || !isEditable) ? value : `${value} & validate`);
                                elementTransformed.setAttribute('readonly.bind', (readonly || !isEditable).toString());
                                elementTransformed.setAttribute('na-option.bind', (naOption || false).toString());
                            }

                            if (defaultValue) {
                                if (defaultValue == "null") {
                                    defaultValue = null;
                                } else if (defaultValue == "true") {
                                    defaultValue = Boolean(defaultValue);
                                } else if (defaultValue == "false") {
                                    defaultValue = false;
                                }
                            } else {
                                defaultValue =  window["defaultOnboardingBooleanValue"] !== undefined ?  window["defaultOnboardingBooleanValue"] : false;
                            }
                        }
                            break;
                        case DataType.Default:
                        case DataType.Password:
                            {
                                elementTransformed = multiline ? document.createElement('text-area') : document.createElement('text-box');
                                elementTransformed.setAttribute('readonly.bind', (readonly || !isEditable).toString());
                                if (type == DataType.Password) {
                                    elementTransformed.setAttribute('type', 'password');
                                    if (readonly || !isEditable) {
                                        elementTransformed.setAttribute('value', "******");
                                    } else {
                                        elementTransformed.setAttribute('value.two-way', `${value} & validate`);
                                    }
                                    if (togglePasswordEnabled) {
                                        elementTransformed.setAttribute('enable-toggle-password.bind', 'true');
                                    }
                                    elementTransformed.setAttribute('textchange.delegate', 'model.passChanged($event)');
                                } else {
                                    elementTransformed.setAttribute('value.two-way', !forceValidations && (readonly || !isEditable) ? value : `${value} & validate`);
                                }
                                if (placeholder) {
                                    elementTransformed.setAttribute('placeholder', placeholder);
                                }
                                if (startLabel) {
                                    elementTransformed.setAttribute('start-label', startLabel);
                                }
                                if (endLabel) {
                                    elementTransformed.setAttribute('end-label', endLabel);
                                }
                            }
                            break;
                        case DataType.Number: {
                            if (!textboxStyle || textboxStyle == "TextBox") {
                                elementTransformed = document.createElement('text-box');
                                elementTransformed.setAttribute('value.two-way', !forceValidations && (readonly || !isEditable) ? value : `${value} & validate`);
                                elementTransformed.setAttribute('blur.delegate',`model.onBlur('${propName}', $event.detail)`);
                                elementTransformed.setAttribute('type', 'integer');
                                elementTransformed.setAttribute('readonly.bind', (readonly || !isEditable).toString());
                                if (placeholder) {
                                    elementTransformed.setAttribute('placeholder', placeholder);
                                }
                                if (startLabel) {
                                    elementTransformed.setAttribute('start-label', startLabel);
                                }
                                if (endLabel) {
                                    elementTransformed.setAttribute('end-label', endLabel);
                                }
                                if (useNumberGrouping != null) {
                                    elementTransformed.setAttribute('use-number-grouping.bind', useNumberGrouping.toString());
                                }
                            } else if (textboxStyle == "Slider" || textboxStyle == "GroupButtons") {
                                elementTransformed = document.createElement('slider');
                                elementTransformed.setAttribute('value.two-way', !forceValidations && (readonly || !isEditable) ? value : `${value} & validate`);
                                elementTransformed.setAttribute('disabled.bind', (readonly || !isEditable).toString());
                                elementTransformed.setAttribute('min.bind', min);
                                elementTransformed.setAttribute('max.bind', max);
                                elementTransformed.setAttribute('step.bind', step);
                                elementTransformed.setAttribute('is-group-buttons.bind', (textboxStyle == "GroupButtons").toString());
                            }
                        }
                            break;
                        case DataType.Double: {
                            elementTransformed = document.createElement('text-box');
                            elementTransformed.setAttribute('value.two-way', !forceValidations && (readonly || !isEditable) ? value : `${value} & validate`);
                            elementTransformed.setAttribute('type', 'double');
                            elementTransformed.setAttribute('readonly.bind', (readonly || !isEditable).toString());
                            if (placeholder) {
                                elementTransformed.setAttribute('placeholder', placeholder);
                            }
                            if (startLabel) {
                                elementTransformed.setAttribute('start-label', startLabel);
                            }
                            if (endLabel) {
                                elementTransformed.setAttribute('end-label', endLabel);
                            }
                        }
                            break;
                        case DataType.Lookup: {
                            if (!lookupStyle || lookupStyle == "dropdown") {
                                elementTransformed = document.createElement('lookup-control');
                                elementTransformed.setAttribute('query', parameter);
                                if(hideEmptyOption != "true")
                                {
                                    elementTransformed.setAttribute('no-item-option-label', ' ');
                                }
                                elementTransformed.setAttribute('show-on-adorner.bind', 'true');
                                elementTransformed.setAttribute('parameters', lookupParameters);
                                if (placeholder) {
                                    elementTransformed.setAttribute('placeholder', placeholder);
                                }
                                elementTransformed.setAttribute('readonly.bind', readonly.toString());
                                elementTransformed.setAttribute('selected-value.two-way', !forceValidations && (readonly || !isEditable) ? value : `${value} & validate`);
                            } else {
                                elementTransformed = document.createElement('form-lookup-control');
                                elementTransformed.setAttribute('query', parameter);
                                elementTransformed.setAttribute('parameters', lookupParameters);
                                elementTransformed.setAttribute('readonly.bind', readonly.toString());
                                elementTransformed.setAttribute('selected-value.two-way', !forceValidations && (readonly || !isEditable) ? value : `${value} & validate`);
                            }
                        }
                            break;
                        case DataType.HierarchicalLookup: {
                            elementTransformed = document.createElement('lookup-hierarchy-control');
                            elementTransformed.setAttribute('query', parameter);
                            elementTransformed.setAttribute('show-on-adorner.bind', 'true');
                            elementTransformed.setAttribute('parameters', lookupParameters);
                            if (placeholder) {
                                elementTransformed.setAttribute('placeholder', placeholder);
                            }
                            elementTransformed.setAttribute('readonly.bind', readonly.toString());
                            elementTransformed.setAttribute('selected-value.two-way', !forceValidations && (readonly || !isEditable) ? value : `${value} & validate`);
                        }
                            break;
                        case DataType.Date:
                        case DataType.DateTime:
                            {
                                if (!format) {
                                    format = type == DataType.DateTime ? ConfigService.instance().config.common.defaultDateTimeFormat : ConfigService.instance().config.common.defaultDateFormat;
                                }
                                elementTransformed = document.createElement('date-picker');
                                elementTransformed.setAttribute('class', 'onboarding-client-variable ' + propName);
                                elementTransformed.setAttribute('date.two-way', !forceValidations && (readonly || !isEditable) ? value : `${value} & validate`);
                                elementTransformed.setAttribute('readonly.bind', (readonly || !isEditable).toString());
                                elementTransformed.setAttribute('date-format', format);
                                elementTransformed.setAttribute('placeholder', format);
                            }
                            break;
                        case DataType.EntityFinder:
                            {
                                elementTransformed = document.createElement('entity-finder-search');
                                elementTransformed.setAttribute('class', 'entity-finder-search-global-search ' + propName);
                                elementTransformed.setAttribute('supported-entity-types.bind', "[" + this.getEntityFinderTypes(props[propName].parameter).join(",") + "]");
                                elementTransformed.setAttribute('disable-search-popup.bind', 'true');
                                elementTransformed.setAttribute('allow-selection.bind', 'true');
                                elementTransformed.setAttribute('format', format);
                                elementTransformed.setAttribute('params', entityFinderParameters)
                                elementTransformed.setAttribute('selected-entity-id.two-way', (readonly || !isEditable) ? value : `${value} & validate`);
                                elementTransformed.setAttribute('readonly.bind', readonly.toString());
                            }
                            break;
                    }
                    model[propName] = defaultValue !== undefined && model[propName] === undefined && element.hasAttribute('data-default-value') ? defaultValue : model[propName];
                }
                if (isCollection) {

                    if (!model[propName]) {
                        model[propName] = [];
                    }
                    model[propName + "_obj"] = {};
                    model[propName].forEach((id: number) => {
                        model[propName + "_obj"][id] = true;
                    });

                    if (type != DataType.Lookup && type != DataType.HierarchicalLookup) {
                        elementTransformed = document.createElement('tags-input-filter');
                        elementTransformed.setAttribute('tags.bind', !forceValidations && (readonly || !isEditable) ? propName : `${propName} & validate`);
                        elementTransformed.setAttribute('readonly.bind', (readonly || !isEditable).toString());
                        elementTransformed.setAttribute('tag-max-length', "250");
                        elementTransformed.setAttribute('max-number-of-tags', "100");
                    } else {
                        const lookupObj = "model." + propName + "_obj";
                        elementTransformed = document.createElement('multiple-lookups-control');
                        elementTransformed.setAttribute('values.two-way', lookupObj);
                        elementTransformed.setAttribute('internal-values.bind', !forceValidations && (readonly || !isEditable) ? propName : `model.${propName} & validate`);
                        elementTransformed.setAttribute('readonly.bind', (readonly || !isEditable).toString());
                        elementTransformed.setAttribute('query', parameter);
                        elementTransformed.setAttribute('is-checkbox-style.bind', (!multiLookupStyle || multiLookupStyle == 'check-boxes').toString());
                        elementTransformed.setAttribute('values-changed.delegate', `model.onMultiLookupChanged('${propName}')`);
                        elementTransformed.setAttribute('exclusive-answers', exclusiveAnswers);
                    }
                }

                elementTransformed.setAttribute('class', 'onboarding-client-variable ' + className);

                if (mobileSize && !readonly) {
                    elementTransformed.classList.add('col-' + mobileSize);
                }
                if (tabletSize && !readonly) {
                    elementTransformed.classList.add('col-sm-' + tabletSize);
                }
                if (desktopSize && !readonly) {
                    elementTransformed.classList.add('col-md-' + desktopSize);
                }
                elementTransformed.classList.add('transformed-element');
                if (element && element.parentNode) {
                    element.parentNode.insertBefore(elementTransformed, element.nextSibling);
                    element.parentNode.removeChild(element);
                }

                if (callback) {
                    callback(elementTransformed, propName, type);
                }
            }
        }
        return div;
    }

    public static getEntityFinderTypes(type: string) {
        return [JSON.stringify(AllEntityTypeConfigurationConstants.filter(x => x.uri == type)[0])];
    }

    public static wrapSpan(node: Node, index: number, textToolTip: string) {
        if (node.nodeName === '#text') {
            const text = node.textContent;
            const s = document.createElement('span');
            s.classList.add('text-node');
            
            //we want to generate a unique name which starts with a letter and does not include '-'
            let refName = 'a' + Guid.generateGuid();
            while(refName.indexOf('-') > -1){
                refName = refName.replace('-', '');
            }

            const toolTipHtml = textToolTip ? 
                `<comp-icon ref="${refName}" class="icon-info" icon="info"></comp-icon>
                <comp-tooltip on-element.bind="${refName}" position="top-left" show-arrow="true">${textToolTip}</comp-tooltip>`:'';

            s.innerHTML = text + toolTipHtml;
            if(node && node.parentNode){
                node.parentNode.insertBefore(s, node.parentNode.childNodes[index]);
                node.parentNode.removeChild(node);
            }
        } else if (!(node instanceof HTMLElement) || !node.classList || !node.classList.contains('variable-placeholder')) {
            const length = node.childNodes.length;
            for (let i = 0; i < length; i++)
                this.wrapSpan(node.childNodes[i], i, textToolTip);
        }
    }
}
